'use client';

import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';

import { tenantAtom } from '@core/Atoms/Tenant/Tenant.atom';
import { canShowDiscount } from '@core/Utils/Vehicle/CanShowDiscount';
import { Logo } from '@gds/Logo/Logo';
import { TotalPrice } from '@gds/TotalPrice/TotalPrice';
import { Typography } from '@gds/Typography/Typography';
import { AdminFee } from '@shared-components/AdminFee/AdminFee';
import PriceRatingGauge from '@vehicle-tile/Components/PriceRatingGauge/PriceRatingGauge';

import { FinancingDetails } from './FinancingDetails/FinancingDetails';
import type { VehicleAttributesProps } from './VehicleAttributes.entity';
import { VehicleName } from './VehicleName';

import styles from './VehicleAttributes.module.css';
export const VehicleAttributes = ({
  locale,
  vehicle,
  strings,
  pageType,
  index,
  hasCampaignDiscount,
  priceRatingStrings,
  isStockUnavailable,
}: VehicleAttributesProps) => {
  const {
    prettyName,
    variant,
    pricing,
    spec,
    ecom: { isEcom },
  } = vehicle;

  const financeRate = vehicle.finance?.rate;
  const showLenderNote = financeRate !== undefined;

  const showDiscount = canShowDiscount(pricing);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  const tenant = useAtomValue(tenantAtom);

  const validPriceRating = vehicle.pricing.priceRating !== 'high';

  const canShowGauge = vehicle.pricing.priceRating && validPriceRating;

  const canShowVspDisclaimer = tenant !== 'rac' && spec.d2c && isEcom;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80 && window.innerWidth >= 1024) {
        setIsScrolling(true);
      } else {
        setIsScrolling(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={styles.attributeWrapperSidebar}>
      <VehicleName name={prettyName} variant={variant} spec={spec} isScrolling={isScrolling} />
      <div
        className={`${styles.scrollWrapper} ${
          showDiscount && isScrolling ? styles.discountAndScrolled : ''
        }`}
        data-scrolled={!showDiscount && isScrolling}
      >
        <div className={styles.priceWrapper} data-variant={'default'}>
          <div className={styles.priceAttributes}>
            <div className={styles.inlineVAT}>
              <TotalPrice
                pricing={pricing}
                strings={strings}
                isScrolling={isScrolling}
                hasCampaignDiscount={hasCampaignDiscount}
                variant={'default'}
                shallShowVatText={false}
              />
              {vehicle.pricing?.isVatAdded && (
                <Typography Component="span" variant="caption2">
                  {strings.vatIncluded}
                </Typography>
              )}
            </div>
            {canShowGauge && (
              <PriceRatingGauge
                locale={locale}
                rating={vehicle.pricing.priceRating!}
                strings={priceRatingStrings}
                clickable={true}
              />
            )}
          </div>
          <AdminFee vehicle={vehicle} source="pdp" />
          {vehicle.pricing?.hasFinance && (
            <Typography className={styles.financeDetails} data-variant={'default'}>
              <FinancingDetails
                strings={strings}
                vehicle={vehicle}
                pageType={pageType}
                index={index}
                isStockUnavailable={isStockUnavailable}
                isFinanceFirst={false}
                isScrolling={isScrolling}
                hasDiscount={!!showDiscount}
              />
            </Typography>
          )}
        </div>
      </div>
      {showLenderNote && (
        <Typography
          className={styles.smallPrint}
          Component="span"
          variant={canShowVspDisclaimer ? 'body4' : 'body2'}
        >
          {strings.lenderNote}
        </Typography>
      )}

      {canShowVspDisclaimer && (
        <div className={styles.contact}>
          <Typography Component="span" variant="body2">
            {strings.investorsStockQuestion}
          </Typography>
          <Typography Component="span" variant="body4" className={styles.logo}>
            {strings.investorsStockContactHeycar} <Logo usage="light_bg" fontSize={80} />
          </Typography>
        </div>
      )}
    </div>
  );
};
